import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface CartCustomization {
    material_id?: number;
    color_id?: number;
    size_id?: number;
    custom_text?: string;
    custom_logo?: string;
    custom_pattern?: string;
    notes?: string;
}

export interface CartItem {
    id: string; // Unique identifier for cart item (product_id + customization hash)
    product_id: number;
    product_name: string;
    product_image?: string;
    base_price: number;
    quantity: number;
    unit_price: number; // Base price + customization modifiers
    total_price: number; // unit_price * quantity
    customization: CartCustomization;
    material_name?: string;
    color_name?: string;
    color_hex?: string;
    size_name?: string;
}

export interface CartState {
    items: CartItem[];
    total_items: number;
    subtotal: number;
    tax_amount: number;
    shipping_cost: number;
    total_amount: number;
    
    // Actions
    addItem: (item: Omit<CartItem, 'id' | 'total_price'>) => void;
    updateQuantity: (itemId: string, quantity: number) => void;
    removeItem: (itemId: string) => void;
    clearCart: () => void;
    calculateTotals: () => void;
    getItemCount: () => number;
    getItemById: (itemId: string) => CartItem | undefined;
    hasItem: (productId: number, customization: CartCustomization) => boolean;
}

// Helper function to generate unique cart item ID
const generateCartItemId = (productId: number, customization: CartCustomization): string => {
    const customizationString = JSON.stringify(customization);
    return `${productId}_${btoa(customizationString).replace(/[^a-zA-Z0-9]/g, '')}`;
};

// Helper function to calculate tax (you can adjust this based on your tax logic)
const calculateTax = (subtotal: number): number => {
    const TAX_RATE = 0.08; // 8% tax rate - adjust as needed
    return subtotal * TAX_RATE;
};

// Helper function to calculate shipping (you can adjust this based on your shipping logic)
const calculateShipping = (subtotal: number, itemCount: number): number => {
    if (subtotal >= 100) return 0; // Free shipping over $100
    if (itemCount === 0) return 0;
    return 15; // Flat rate shipping
};

const useCartStore = create<CartState>()(
    persist(
        (set, get) => ({
            items: [],
            total_items: 0,
            subtotal: 0,
            tax_amount: 0,
            shipping_cost: 0,
            total_amount: 0,

            addItem: (newItem) => {
                const state = get();
                const itemId = generateCartItemId(newItem.product_id, newItem.customization);
                
                // Check if item already exists
                const existingItemIndex = state.items.findIndex(item => item.id === itemId);
                
                let updatedItems: CartItem[];
                
                if (existingItemIndex >= 0) {
                    // Update quantity of existing item
                    updatedItems = state.items.map((item, index) => 
                        index === existingItemIndex 
                            ? { 
                                ...item, 
                                quantity: item.quantity + newItem.quantity,
                                total_price: (item.quantity + newItem.quantity) * item.unit_price
                              }
                            : item
                    );
                } else {
                    // Add new item
                    const cartItem: CartItem = {
                        ...newItem,
                        id: itemId,
                        total_price: newItem.unit_price * newItem.quantity,
                    };
                    updatedItems = [...state.items, cartItem];
                }
                
                set({ items: updatedItems });
                get().calculateTotals();
            },

            updateQuantity: (itemId, quantity) => {
                const state = get();
                
                if (quantity <= 0) {
                    get().removeItem(itemId);
                    return;
                }
                
                const updatedItems = state.items.map(item =>
                    item.id === itemId
                        ? { ...item, quantity, total_price: item.unit_price * quantity }
                        : item
                );
                
                set({ items: updatedItems });
                get().calculateTotals();
            },

            removeItem: (itemId) => {
                const state = get();
                const updatedItems = state.items.filter(item => item.id !== itemId);
                set({ items: updatedItems });
                get().calculateTotals();
            },

            clearCart: () => {
                set({
                    items: [],
                    total_items: 0,
                    subtotal: 0,
                    tax_amount: 0,
                    shipping_cost: 0,
                    total_amount: 0,
                });
            },

            calculateTotals: () => {
                const state = get();
                const subtotal = state.items.reduce((sum, item) => sum + item.total_price, 0);
                const total_items = state.items.reduce((sum, item) => sum + item.quantity, 0);
                const tax_amount = calculateTax(subtotal);
                const shipping_cost = calculateShipping(subtotal, total_items);
                const total_amount = subtotal + tax_amount + shipping_cost;
                
                set({
                    total_items,
                    subtotal,
                    tax_amount,
                    shipping_cost,
                    total_amount,
                });
            },

            getItemCount: () => {
                return get().total_items;
            },

            getItemById: (itemId) => {
                return get().items.find(item => item.id === itemId);
            },

            hasItem: (productId, customization) => {
                const itemId = generateCartItemId(productId, customization);
                return get().items.some(item => item.id === itemId);
            },
        }),
        {
            name: "cart-storage",
            partialize: (state) => ({
                items: state.items,
                total_items: state.total_items,
                subtotal: state.subtotal,
                tax_amount: state.tax_amount,
                shipping_cost: state.shipping_cost,
                total_amount: state.total_amount,
            }),
        }
    )
);

export default useCartStore;
